import React, { createContext, useState, useEffect } from 'react';
import enTranslations from './translations/en.json';
import deTranslations from './translations/de.json';
import esTranslations from './translations/es.json';
import frTranslations from './translations/fr.json';
import itTranslations from './translations/it.json';
import ptTranslations from './translations/pt.json';

// Create the localization context
export const LocalizationContext = createContext();

/**
 * Get a nested property from an object using a dot-notation path
 * @param {Object} obj - The object to get the property from
 * @param {string} path - The path to the property (e.g. 'common.button.submit')
 * @param {Object} params - Optional parameters to interpolate into the string
 * @returns {string} The value at the path, or the path itself if not found
 */
const getNestedProperty = (obj, path, params = {}) => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return path; // Return the path if the property doesn't exist
    }
  }
  
  // If the result is a string, interpolate any parameters
  if (typeof result === 'string') {
    return Object.entries(params).reduce((str, [key, value]) => {
      return str.replace(new RegExp(`{{${key}}}`, 'g'), value);
    }, result);
  }
  
  return result || path;
};

// Available languages
export const languages = {
  en: { name: 'English', translations: enTranslations },
  de: { name: 'Deutsch', translations: deTranslations },
  es: { name: 'Español', translations: esTranslations },
  fr: { name: 'Français', translations: frTranslations },
  it: { name: 'Italiano', translations: itTranslations },
  pt: { name: 'Português', translations: ptTranslations },
};

// Default language
const DEFAULT_LANGUAGE = 'en';

/**
 * Provider component for localization
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const LocalizationProvider = ({ children }) => {
  // Try to get the language from localStorage, or use browser language, or fallback to default
  const getBrowserLanguage = () => {
    const browserLang = navigator.language.split('-')[0];
    return languages[browserLang] ? browserLang : DEFAULT_LANGUAGE;
  };
  
  const [language, setLanguage] = useState(() => {
    try {
      const storedLang = localStorage.getItem('language');
      return storedLang || getBrowserLanguage();
    } catch (error) {
      return DEFAULT_LANGUAGE;
    }
  });
  
  // Get translations for the current language, fallback to English
  const translations = languages[language]?.translations || languages[DEFAULT_LANGUAGE].translations;
  
  // Function to change the language
  const changeLanguage = (lang) => {
    if (languages[lang]) {
      setLanguage(lang);
      try {
        localStorage.setItem('language', lang);
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };
  
  // Translation function
  const t = (key, params = {}) => {
    return getNestedProperty(translations, key, params);
  };
  
  // Context value
  const contextValue = {
    language,
    languages,
    changeLanguage,
    t,
  };
  
  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
};

// Export the LanguageSelector component and useLocalization hook
export { default as LanguageSelector } from './LanguageSelector';
export { default as useLocalization } from './useLocalization';
